import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useI18n } from '../context/I18nContext';
import UserAvatar from '../components/UserAvatar';
import { formatAura } from '../lib/aura';

const CAT_LABEL_ES = { social_action:'Acción social', civic:'Buen ciudadano', solidarity:'Solidaridad', sports:'Deportiva', leadership:'Liderazgo', teamwork:'Trabajo en equipo', creativity:'Creatividad', academic:'Académica', artistic:'Artística', environmental:'Ambiental', bravery:'Valentía', honesty:'Honestidad', respect:'Respeto', discipline:'Disciplina', self_improvement:'Superación', other:'Otra' };

export default function RecognizePage() {
  const { token } = useParams();
  const nav = useNavigate();
  const { t } = useI18n();
  const [target, setTarget] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [category, setCategory] = useState('solidarity');
  const [amount, setAmount] = useState(50);
  const [description, setDescription] = useState('');
  const [witnesses, setWitnesses] = useState(['', '']);
  const [location, setLocation] = useState('');
  const [ethical, setEthical] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [rQR, rCfg] = await Promise.all([
          api.get(`/qr/u/${token}`),
          api.get('/community/config'),
        ]);
        setTarget(rQR.data);
        setCfg(rCfg.data);
        setAmount(rCfg.data.allowed_amounts[2] || 50);
      } catch (err) { toast.error(err?.response?.data?.detail || t('common.error')); nav(-1); }
    })();
  }, [token, nav, t]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const cleanWits = witnesses.map((w) => w.replace(/^@/, '').trim()).filter(Boolean);
    if (cleanWits.length < 2) { toast.error('Necesitas 2 testigos'); return; }
    if (description.trim().length < (cfg?.min_description_len || 30)) { toast.error(`Descripción mínima: ${cfg?.min_description_len} caracteres`); return; }
    if (!ethical) { toast.error('Debes aceptar la Declaración Aura'); return; }
    setBusy(true);
    try {
      await api.post('/recognitions', {
        receiver_qr_token: token,
        category, description: description.trim(),
        proposed_aura_points: amount,
        witness_usernames: cleanWits,
        location: location || null,
        ethical_declaration: true,
      });
      toast.success('Reconocimiento enviado — esperando testigos');
      nav('/community/mine', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.error'));
    } finally { setBusy(false); }
  };

  if (!target || !cfg) return <div className="min-h-screen grid place-items-center bg-aura-bg"><div className="text-4xl animate-aura-pulse">⚡</div></div>;

  const u = target.user;
  return (
    <div className="min-h-screen bg-aura-bg pb-16 relative">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <header className="relative flex items-center justify-between px-5 pt-6">
        <button onClick={() => nav(-1)} className="text-aura-textDim flex items-center gap-1"><ChevronLeft size={20} /> Volver</button>
        <span className="w-8" />
      </header>

      <main className="relative mx-auto max-w-md px-5 pt-2 space-y-4">
        <div className="text-center">
          <Sparkles size={28} className="text-aura-cyan mx-auto animate-aura-pulse" />
          <div className="font-display font-black text-2xl aura-text-gradient tracking-tight">OTORGAR AURA</div>
          <div className="text-[10px] uppercase tracking-widest text-aura-yellow mt-1">EL AURA SE GANA, NO SE REGALA</div>
        </div>

        {/* Target */}
        <div className="aura-card p-4 flex items-center gap-3">
          <UserAvatar user={u} size="md" />
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-white truncate">{u.name}</div>
            <div className="text-xs text-aura-textDim truncate">@{u.username}</div>
            <div className="text-xs text-aura-cyan font-mono">⚡ {formatAura(u.aura_points || 0)}</div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {/* Category */}
          <div>
            <div className="text-[11px] uppercase tracking-widest text-aura-textDim mb-2">Categoría</div>
            <div className="flex gap-1.5 flex-wrap">
              {cfg.categories.map((c) => (
                <button type="button" key={c} onClick={() => setCategory(c)} data-testid={`cat-${c}`}
                        className={`aura-chip !text-[11px] ${category === c ? 'border-aura-cyan text-aura-cyan' : ''}`}>
                  {CAT_LABEL_ES[c] || c}
                </button>
              ))}
            </div>
          </div>

          {/* Amount */}
          <div>
            <div className="text-[11px] uppercase tracking-widest text-aura-textDim mb-2">Aura</div>
            <div className="grid grid-cols-5 gap-2">
              {cfg.allowed_amounts.map((a) => (
                <button key={a} type="button" onClick={() => setAmount(a)} data-testid={`amount-${a}`}
                        className={`rounded-xl py-2 font-display font-bold text-sm border ${amount === a ? 'border-aura-cyan text-aura-cyan bg-aura-cyan/10' : 'border-white/10 text-white/80'}`}>
                  +{a}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <div className="text-[11px] uppercase tracking-widest text-aura-textDim mb-2">¿Por qué? (mín {cfg.min_description_len} caracteres)</div>
            <textarea data-testid="desc" className="aura-input min-h-[100px] py-2" value={description}
                      onChange={(e) => setDescription(e.target.value)} maxLength={500}
                      placeholder="Describe con detalle la acción que presenciaste…" />
            <div className="text-[10px] text-aura-textDim text-right mt-1">{description.length}/500</div>
          </div>

          {/* Witnesses */}
          <div>
            <div className="text-[11px] uppercase tracking-widest text-aura-textDim mb-2">Testigos (mínimo 2)</div>
            <div className="space-y-2">
              {witnesses.map((w, i) => (
                <div key={i} className="flex gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-aura-textDim">@</span>
                    <input data-testid={`wit-${i}`} className="aura-input pl-7" value={w}
                           onChange={(e) => setWitnesses((prev) => prev.map((x, j) => j === i ? e.target.value : x))} />
                  </div>
                  {witnesses.length > 2 && (
                    <button type="button" onClick={() => setWitnesses((prev) => prev.filter((_, j) => j !== i))} className="text-red-400/70 px-2"><X size={16} /></button>
                  )}
                </div>
              ))}
              {witnesses.length < 10 && (
                <button type="button" onClick={() => setWitnesses((prev) => [...prev, ''])} className="text-xs text-aura-cyan">+ agregar testigo</button>
              )}
            </div>
          </div>

          {/* Location optional */}
          <div>
            <div className="text-[11px] uppercase tracking-widest text-aura-textDim mb-2">Ubicación (opcional)</div>
            <input data-testid="loc" className="aura-input" value={location} onChange={(e) => setLocation(e.target.value)} maxLength={120} placeholder="Parque Metropolitano…" />
          </div>

          {/* Ethical declaration */}
          <div className="aura-card p-4">
            <div className="text-[11px] uppercase tracking-widest text-aura-yellow font-display font-bold mb-2">Declaración Aura</div>
            <p className="text-xs text-white/80 mb-3">
              Confirmo que presencié o tengo conocimiento directo de esta acción y que estoy otorgando estos Aura Points de buena fe.
              Otorgar puntos sin sustento, mediante acuerdos, favores o información inventada constituye una violación al Movimiento Aura.
            </p>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="checkbox" data-testid="ethical" checked={ethical} onChange={(e) => setEthical(e.target.checked)} className="mt-0.5 h-4 w-4 accent-aura-cyan" />
              <span>Confirmo que este reconocimiento es auténtico.</span>
            </label>
          </div>

          <button disabled={busy || !ethical} data-testid="submit-recognition" className="aura-btn-primary w-full h-12 disabled:opacity-50">
            {busy ? 'Enviando…' : `Otorgar +${amount} AURA`}
          </button>
        </form>
      </main>
    </div>
  );
}
